import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Box, Typography, Button, Alert, Spinner, Breadcrumbs, Chip } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import { VendorApiError, vendorApiClient } from '../../lib/vendorApiClient';
import { readSpreadsheet, SpreadsheetError, type ParsedSheet } from '../../lib/spreadsheet';
import { LOCATION_TEMPLATE_HEADERS, locationTemplateCsv, pieces, tally, type LocationRowResult } from '../../lib/locationImport';

/**
 * L4 — Import locations (§15ii, B1). A vendor with many stores sends them as
 * one file. We read it here, say what we found before adding anything, and
 * each location that is added waits for a person to verify it — the same as
 * one added by hand.
 */

const HEADING = '#303A42';
const MUTED = '#5A7063';
const BORDER = '#E5E5E5';
const GREEN_INK = '#1F7026';
const ORANGE_INK = '#A85200';
const RED = '#C62828';

type Stage = 'pick' | 'checked' | 'adding' | 'done';

/** A row of the file, with where it is and what we made of it. */
interface Line extends LocationRowResult {
  sheet: number;
}

function problemText(t: TFunction, line: LocationRowResult): string {
  return line.code ? t(`locations.import.problem.${line.code}`, { value: line.value ?? '', defaultValue: t('locations.errors.generic') }) : '';
}

export default function LocationImport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeVendorId, has } = useVendor();
  const canManage = has('location:manage');
  const input = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<Stage>('pick');
  const [fileName, setFileName] = useState('');
  const [sheets, setSheets] = useState<ParsedSheet[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  if (!activeVendorId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const describe = (e: unknown): string => {
    if (e instanceof SpreadsheetError) return t(`products.import.fileProblem.${e.problem}`, { file: e.file });
    if (e instanceof VendorApiError && e.code === 'columns_missing') return t('locations.import.columnsMissing');
    if (e instanceof VendorApiError && e.code) return t(`locations.errors.${e.code}`, { defaultValue: e.message });
    return e instanceof Error && e.message ? e.message : t('locations.errors.generic');
  };

  /** Reads the file and asks what we can and cannot use — nothing is added yet. */
  const check = async (file: File) => {
    setBusy(true);
    setError(null);
    setLines([]);
    try {
      const read = await readSpreadsheet(file);
      const found: Line[] = [];
      for (const [i, sheet] of read.entries()) {
        const { results } = await vendorApiClient.importLocations(activeVendorId, { headers: sheet.headers, rows: sheet.rows, check: true });
        found.push(...results.map((r) => ({ ...r, sheet: i })));
      }
      setFileName(file.name);
      setSheets(read);
      setLines(found);
      setStage('checked');
    } catch (e) {
      setError(describe(e));
      setStage('pick');
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const ready = lines.filter((l) => l.status === 'ready');
    setStage('adding');
    setError(null);
    setProgress({ done: 0, total: ready.length });
    const outcome = new Map<string, LocationRowResult>();
    try {
      for (const [i, sheet] of sheets.entries()) {
        const wanted = new Set(ready.filter((l) => l.sheet === i).map((l) => l.r));
        for (const piece of pieces(sheet.rows.filter((row) => wanted.has(row.r)))) {
          const { results } = await vendorApiClient.importLocations(activeVendorId, { headers: sheet.headers, rows: piece });
          for (const r of results) outcome.set(`${i}:${r.r}`, r);
          setProgress((p) => ({ ...p, done: p.done + piece.length }));
        }
      }
    } catch (e) {
      // What was added stays added; the rest can be sent again and is skipped if it is already there.
      setError(t('locations.import.stopped', { reason: describe(e) }));
    }
    setLines((prev) => prev.map((l) => ({ ...l, ...(outcome.get(`${l.sheet}:${l.r}`) ?? (l.status === 'ready' ? { status: 'refused' as const, code: 'not_sent' } : {})) })));
    setStage('done');
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([locationTemplateCsv()], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = t('locations.import.template.fileName');
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setStage('pick');
    setSheets([]);
    setLines([]);
    setFileName('');
    setError(null);
  };

  const counts = tally(lines);
  const tabName = (line: Line) => (sheets.length > 1 && sheets[line.sheet]?.sheet ? `${t('products.questions.place.tab', { tab: sheets[line.sheet].sheet })} · ` : '');

  const crumbs = (
    <Breadcrumbs
      ariaLabel={t('locations.breadcrumbAria')}
      linkComponent={Link}
      items={[
        { label: t('locations.title'), to: '/locations' },
        { label: t('locations.importLocations'), ariaCurrent: true },
      ]}
    />
  );

  if (!canManage) {
    return (
      <Box>
        {crumbs}
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('locations.errors.noManage')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {crumbs}
      <Typography component="h1" sx={{ mt: 2, fontSize: 40, lineHeight: 1.2, fontWeight: 700, color: HEADING }}>
        {t(stage === 'done' ? 'locations.import.doneTitle' : 'locations.importLocations')}
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 16, color: MUTED }}>{t(stage === 'done' ? 'locations.import.doneSubtitle' : 'locations.import.subtitle')}</Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="location-import-error">
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, gap: 3, alignItems: 'start' }}>
        <Box sx={{ p: 3, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px', minWidth: 0 }}>
          <input
            ref={input}
            type="file"
            accept=".xlsx,.csv,.tsv,.txt"
            hidden
            data-testid="location-import-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void check(f);
              e.target.value = '';
            }}
          />

          {stage === 'pick' && (
            <Box
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files[0];
                if (f && !busy) void check(f);
              }}
              sx={{ p: 4, border: '2px dashed #9AA3AD', borderRadius: '8px', bgcolor: '#F7F7F8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center' }}
              data-testid="location-import-drop"
            >
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: HEADING }}>{t('locations.import.drop')}</Typography>
              <Typography sx={{ fontSize: 14, color: '#343434' }}>{t('locations.import.dropHint')}</Typography>
              {busy ? (
                <Spinner color="brand" />
              ) : (
                <Button ariaLabel={t('products.import.upload.browse')} buttonText={t('products.import.upload.browse')} variant="outlinedGreen" onClick={() => input.current?.click()} />
              )}
            </Box>
          )}

          {stage !== 'pick' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                <Typography component="h2" sx={{ flex: 1, fontSize: 21, fontWeight: 600, color: HEADING }}>
                  {stage === 'done'
                    ? t('locations.import.summaryDone', { added: counts.added, count: lines.length })
                    : t('locations.import.summaryChecked', { ready: counts.ready, count: lines.length })}
                </Typography>
                <Typography sx={{ fontSize: 14, color: MUTED, overflowWrap: 'anywhere' }}>{fileName}</Typography>
              </Box>
              {stage === 'checked' && counts.refused > 0 && (
                <Typography sx={{ mt: 0.5, fontSize: 15, color: MUTED }}>{t('locations.import.someRefused', { count: counts.refused })}</Typography>
              )}

              <Box role="table" aria-label={t('locations.import.tableAria')} sx={{ mt: 2 }} data-testid="location-import-rows">
                {lines.map((line, i) => (
                  <Box
                    role="row"
                    key={`${line.sheet}:${line.r}`}
                    data-testid={`location-import-row-${line.sheet}-${line.r}`}
                    sx={{ py: 1.5, borderTop: i === 0 ? `1px solid ${BORDER}` : 'none', borderBottom: `1px solid ${BORDER}`, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(0, 1.6fr) 150px' }, gap: { xs: 0.5, md: 2 }, alignItems: 'center' }}
                  >
                    <Box role="cell" sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700, color: HEADING, overflowWrap: 'anywhere' }}>{line.name || t('products.import.done.rowTitle', { row: line.r })}</Typography>
                      <Typography sx={{ fontSize: 13, color: MUTED }}>
                        {tabName(line)}
                        {t('products.questions.place.row', { row: line.r })}
                      </Typography>
                    </Box>
                    <Typography role="cell" sx={{ fontSize: 14, color: line.status === 'refused' ? RED : line.status === 'skipped' ? ORANGE_INK : '#343434' }}>
                      {line.status === 'refused' || line.status === 'skipped' ? problemText(t, line) : line.address}
                    </Typography>
                    <Box role="cell" sx={{ justifySelf: { md: 'end' } }}>
                      <RowChip status={line.status} />
                    </Box>
                  </Box>
                ))}
              </Box>

              {stage === 'adding' && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }} role="status" data-testid="location-import-adding">
                  <Spinner color="brand" />
                  <Typography sx={{ fontSize: 15, color: HEADING }}>{t('locations.import.adding', progress)}</Typography>
                </Box>
              )}

              {stage === 'checked' && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                  <Button ariaLabel={t('locations.import.another')} buttonText={t('locations.import.another')} variant="text" onClick={reset} dataTestId="location-import-reset" />
                  <Button
                    ariaLabel={t('locations.import.addCount', { count: counts.ready })}
                    buttonText={t('locations.import.addCount', { count: counts.ready })}
                    variant="contained"
                    color="primary"
                    disabled={counts.ready === 0}
                    onClick={() => void add()}
                    dataTestId="location-import-add"
                  />
                </Box>
              )}
              {stage === 'done' && (
                <>
                  {counts.added > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {t('locations.import.doneNote')}
                    </Alert>
                  )}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                    <Button ariaLabel={t('locations.import.another')} buttonText={t('locations.import.another')} variant="text" onClick={reset} dataTestId="location-import-reset" />
                    <Button
                      ariaLabel={t('locations.import.seeLocations')}
                      buttonText={t('locations.import.seeLocations')}
                      variant="contained"
                      color="primary"
                      onClick={() => void navigate('/locations')}
                      dataTestId="location-import-see"
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Box>

        <Box sx={{ p: 3, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
          <Typography component="h2" sx={{ fontSize: 21, fontWeight: 600, color: HEADING }}>
            {t('locations.import.columns.title')}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 14, color: MUTED }}>{t('locations.import.columns.intro')}</Typography>
          <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {(['required', 'line2', 'contact', 'hours', 'services', 'fns', 'externalId'] as const).map((k) => (
              <Typography component="li" key={k} sx={{ fontSize: 14, color: '#343434' }}>
                {t(`locations.import.columns.${k}`)}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: 12, color: MUTED, overflowWrap: 'anywhere' }}>{LOCATION_TEMPLATE_HEADERS.join(', ')}</Typography>
          <Box sx={{ mt: 2 }}>
            <Button ariaLabel={t('locations.import.template.download')} buttonText={t('locations.import.template.download')} variant="outlinedGreen" onClick={downloadTemplate} dataTestId="location-import-template" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function RowChip({ status }: { status: LocationRowResult['status'] }) {
  const { t } = useTranslation();
  const spec = {
    ready: { bg: '#EAF5EA', fg: GREEN_INK, border: undefined },
    added: { bg: '#FEF1E7', fg: ORANGE_INK, border: undefined },
    skipped: { bg: '#F1F2F4', fg: '#3C4650', border: undefined },
    refused: { bg: '#FFFFFF', fg: RED, border: RED },
  }[status];
  return (
    <Chip
      label={`● ${t(`locations.import.status.${status}`)}`}
      size="small"
      backgroundColor={spec.bg}
      textColor={spec.fg}
      fontSize={12}
      fontWeight={700}
      sx={spec.border ? { border: `1px solid ${spec.border}` } : undefined}
    />
  );
}
